/**
 * FillerToolbar — Fill / Draw / Text tools for the form filler (freestyle writing on any form).
 */
import { Button } from "./ui/button";
import { MousePointer2, PenLine, Type, Eraser } from "lucide-react";

const COLORS = [
  ["#111827", "Black"],
  ["#1d4ed8", "Blue"],
  ["#dc2626", "Red"],
];

export default function FillerToolbar({ tool, setTool, penColor, setPenColor, penWidth, setPenWidth, inkMode, setInkMode, eraserWidth, setEraserWidth }) {
  return (
    <div className="px-3 py-1.5 border-b bg-slate-50 flex items-center gap-2 flex-wrap" data-testid="filler-toolbar">
      <div className="flex rounded-md border overflow-hidden">
        {[
          ["fill", MousePointer2, "Fill", "Tap fields to fill them in"],
          ["draw", PenLine, "Draw", "Handwrite anywhere on the form with a finger, stylus or mouse"],
          ["text", Type, "Text", "Tap anywhere on the form to type a note"],
        ].map(([key, Icon, label, tip]) => (
          <Button
            key={key}
            size="sm"
            variant={tool === key ? "default" : "ghost"}
            onClick={() => setTool(key)}
            className="rounded-none gap-1 h-7 px-2.5 text-xs"
            title={tip}
            data-testid={`tool-${key}-btn`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </Button>
        ))}
      </div>
      {tool === "draw" && (
        <>
          <div className="flex rounded-md border overflow-hidden" data-testid="ink-mode">
            <Button size="sm" variant={inkMode === "pen" ? "default" : "ghost"} onClick={() => setInkMode("pen")} className="rounded-none gap-1 h-7 px-2.5 text-xs" title="Pen" data-testid="ink-mode-pen">
              <PenLine className="w-3.5 h-3.5" /> Pen
            </Button>
            <Button size="sm" variant={inkMode === "eraser" ? "default" : "ghost"} onClick={() => setInkMode("eraser")} className="rounded-none gap-1 h-7 px-2.5 text-xs" title="Eraser — rub out part of a drawing" data-testid="ink-mode-eraser">
              <Eraser className="w-3.5 h-3.5" /> Eraser
            </Button>
          </div>
          {inkMode === "eraser" && (
            <div className="flex items-center gap-1 text-xs text-slate-600" data-testid="eraser-sizes">
              {[[8, "Small"], [16, "Medium"], [32, "Large"]].map(([w, name]) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setEraserWidth(w)}
                  className={`px-2 h-6 rounded border text-[11px] ${eraserWidth === w ? "bg-slate-800 text-white border-slate-800" : "bg-white"}`}
                  data-testid={`eraser-size-${name.toLowerCase()}`}
                >
                  {name}
                </button>
              ))}
              <span className="text-[11px] text-slate-500 hidden md:inline">Rub over any part of the drawing to erase just that part.</span>
            </div>
          )}
          {inkMode === "pen" && <div className="flex items-center gap-1" data-testid="pen-colors">
            {COLORS.map(([c, name]) => (
              <button
                key={c}
                type="button"
                onClick={() => setPenColor(c)}
                className={`w-5 h-5 rounded-full border-2 ${penColor === c ? "border-slate-900 scale-110" : "border-white"} shadow`}
                style={{ backgroundColor: c }}
                title={name}
                aria-label={`${name} pen`}
                data-testid={`pen-color-${name.toLowerCase()}`}
              />
            ))}
          </div>}
          {inkMode === "pen" && <div className="flex items-center gap-1 text-xs text-slate-600">
            {[[1.2, "Fine"], [2, "Medium"], [3.5, "Bold"]].map(([w, name]) => (
              <button
                key={name}
                type="button"
                onClick={() => setPenWidth(w)}
                className={`px-2 h-6 rounded border text-[11px] ${penWidth === w ? "bg-slate-800 text-white border-slate-800" : "bg-white"}`}
                data-testid={`pen-width-${name.toLowerCase()}`}
              >
                {name}
              </button>
            ))}
          </div>}
          {inkMode === "pen" && <span className="text-[11px] text-slate-500 hidden md:inline">Write anywhere on the page. Use the Undo / Clear buttons at the top of each page.</span>}
        </>
      )}
      {tool === "text" && (
        <span className="text-[11px] text-slate-500">Tap a spot on the form to add a note; drag its blue handle to move it.</span>
      )}
    </div>
  );
}
